import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Concierge from './components/Concierge';
import { useLenis } from './lib/lenis';
import { organizationSchema, websiteSchema } from './lib/schema';
import Home from './pages/Home';
import Products from './pages/Products';
import Applications from './pages/Applications';
import Technology from './pages/Technology';
import Gallery from './pages/Gallery';
import About from './pages/About';
import Contact from './pages/Contact';
import ProductDetail from './pages/ProductDetail';
import Collections from './pages/Collections';
import Engineering from './pages/Engineering';
import Materials from './pages/Materials';
import Hospitality from './pages/Hospitality';
import Residential from './pages/Residential';
import Commercial from './pages/Commercial';
import Journal from './pages/Journal';
import JournalCS25 from './pages/JournalCS25';
import FAQ from './pages/FAQ';
import NotFound from './pages/NotFound';
import AdminEnquiries from './pages/AdminEnquiries';
import ScrollToTop from './components/ScrollToTop';
import './App.css';

function Shell() {
  useLenis();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  return (
    <div className="App">
      <ScrollToTop />
      {!isAdmin && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/:slug" element={<ProductDetail />} />
        <Route path="/collections" element={<Collections />} />
        <Route path="/applications" element={<Applications />} />
        <Route path="/hospitality" element={<Hospitality />} />
        <Route path="/residential" element={<Residential />} />
        <Route path="/commercial" element={<Commercial />} />
        <Route path="/technology" element={<Technology />} />
        <Route path="/engineering" element={<Engineering />} />
        <Route path="/materials" element={<Materials />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/journal" element={<Journal />} />
        <Route path="/journal/cs25-capsule-house-india-resorts" element={<JournalCS25 />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/admin/enquiries" element={<AdminEnquiries />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isAdmin && <Footer />}
      {!isAdmin && <Concierge variant="floating" />}
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <Helmet>
        <title>MODULAROOM | Luxury Modular Homes & Prefab Living</title>
        <meta name="description" content="MODULAROOM designs and manufactures luxury modular homes, capsule suites and prefab cottages. Factory-built modular resort rooms, glamping cabins and modular offices delivered worldwide from India." />
        <meta name="keywords" content="luxury modular homes, prefab homes, modular homes India, prefab cottages, modular resort rooms, capsule homes, prefabricated homes, modular offices, glamping cabins, modular hospitality, off-site construction" />
        <meta property="og:title" content="MODULAROOM | Luxury Modular Homes & Prefab Living" />
        <meta property="og:description" content="Factory-built luxury modular spaces. Capsule Suites and Studio Suites for resorts, farmhouses, offices and glamping. Made in India, delivered worldwide." />
        <meta property="og:image" content="/og-image.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="/og-image.jpg" />
        <meta name="theme-color" content="#111111" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema())}</script>
      </Helmet>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </HelmetProvider>
  );
}
